/**
 * The memory tree contract — Law 1 in code.
 *
 * One brain, one head: everything Nami knows lives under a single `memory/`
 * tree on the user volume. The tiers below are the whole vocabulary, and this
 * module is the only place their paths are spelled. Nothing in
 * `apps/{server,sandbox,web}` writes a memory path by hand; a lint in
 * `memory-tree.test.ts` holds that line.
 *
 * The tiers divide by who shapes them:
 *   - `evidence/` and `now/` are fixed skeletons — the code owns their layout,
 *     so it can always find its way back in.
 *   - `knowledge/` is free-form. Pages are created, renamed, and merged as the
 *     user's world changes; renames ride git, and every page stays reachable
 *     from `knowledge/index.md`.
 *
 * Two rules the shape enforces on its own:
 *   - Everything enters through `evidence/` first (Law 2). It is the only door.
 *   - `now/` is never a citation target — there is no `now` door, so a claim
 *     cannot cite the scratchpad (Law 2 again).
 */

export const MEMORY_ROOT = 'memory';

export const TREE = {
  /** The kernel: who the user is. */
  kernel: 'memory/user.md',
  /** Working scratchpad. Fixed skeleton, and never a citation target. */
  now: { root: 'memory/now', week: 'memory/now/week.md', loops: 'memory/now/loops' },
  /** Free-form pages. Create, rename, merge — every page reachable from the index. */
  knowledge: {
    root: 'memory/knowledge',
    index: 'memory/knowledge/index.md',
    log: 'memory/knowledge/log.md',
    learnings: 'memory/knowledge/learnings',
    /**
     * How the agent works for this user: the standing rules the procedures fold
     * keeps. ONE page, rewritten in place, because a standing rule is a single
     * living document rather than a dated record like a learning.
     */
    howYouWork: 'memory/knowledge/how-you-work.md',
  },
  notes: { root: 'memory/notes' },
  /**
   * The one door in. Fixed skeleton; rows are immutable (supersede, never edit).
   *
   * `web` is a page the dreamer fetched to verify a claim (T11, D3) — its id is
   * `sha256(userId, url, contentSha256).slice(0,16)` (Ruling 127), so a citation
   * names ONE user's read of THOSE bytes: re-fetching an unchanged page mints
   * the same citation, and a page that has since changed is new evidence with an
   * id of its own rather than a silent overwrite of what was cited before.
   */
  evidence: {
    root: 'memory/evidence',
    index: 'memory/evidence/index.md',
    doors: ['task', 'artifact', 'upload', 'sense', 'saved', 'web'] as const,
  },
  /**
   * The tree's own ignore file. `evidence/` is listed there: the evidence tier
   * is derived from the store and rebuildable, so committing it would put a
   * second copy of the truth under version control.
   */
  gitignore: 'memory/.gitignore',
  /** Pre-tree wiki, read for one release while old volumes converge. */
  legacyWikiRoot: 'memory/.nami/wiki',
} as const;

export type EvidenceDoor = (typeof TREE.evidence.doors)[number];

/**
 * The heading of the kernel's HOW YOU WORK section, spelled once for both sides of the
 * wire. The server renders it over the person's standing rules (nami-materialize's
 * compileKernel), and the box's memory layer finds it to keep that section whole when
 * the kernel is cut to its budget (nami-now-layer). A rename here moves both.
 */
export const PROCEDURES_HEADING = '## How I work for you';

/**
 * The five tiers, in the order the brain reads top to bottom — THE tier list.
 * The server's tree routes and the web's Brain room both consume it; neither
 * keeps a copy. Every entry is a key of `TREE` (the `satisfies` makes a typo a
 * build error), and the test pins that the list is exactly TREE's tier keys.
 */
export const TREE_TIERS = ['kernel', 'now', 'knowledge', 'notes', 'evidence'] as const satisfies readonly (keyof typeof TREE)[];
export type TreeTier = (typeof TREE_TIERS)[number];

/** The knowledge tier as a page path spells it: `TREE.knowledge.root` without the tree root.
 *  Exported so a sibling contract composing a knowledge-relative path spells it from HERE
 *  rather than keeping a fourth copy of the same slice. */
export const KNOWLEDGE_DIR = TREE.knowledge.root.slice(MEMORY_ROOT.length + 1);

/**
 * THE USER'S OWN FOLDER (Phase 4 Train 1). The six self pages live here, and it is
 * the FIRST seed so the front door, the compiled map and the kernel all read the
 * person before the world. Knowledge-relative like every seed.
 */
export const SELF_SEED_FOLDER = 'self';

/**
 * The folders a brain is seeded with — seeds, not cages (Law 2). The reconciler
 * may create, rename and merge folders as a user's world takes shape, and
 * `pageTypeOf` reads whatever folder a page ended up in; these five are only
 * where it starts. Knowledge-relative, so a page path prefixes them with the
 * knowledge tier.
 */
export const PAGE_SEED_FOLDERS = [
  SELF_SEED_FOLDER,
  'entities/people',
  'entities/companies',
  'projects',
  'concepts',
] as const;

/**
 * What a knowledge page's path may look like: tree-relative (the tree root is
 * `memory/`, which page paths omit), one to three folders below `knowledge/`,
 * a markdown slug at the end.
 *
 * The alphabet is the jail. `[a-z0-9-]` holds no `.` and no `/`, so `..`, `.`,
 * a hidden segment and an absolute path are unspellable rather than separately
 * forbidden — there is no second rule to keep in step with this one. No segment
 * may open with a dash either: host code hands these paths to `git mv`, and a
 * name that reads as an option there has no legitimate use here.
 *
 * Two shapes it deliberately refuses:
 *   - a file sitting directly under `knowledge/` — `index.md`, `log.md` and
 *     `how-you-work.md` are written by deterministic compilers, not filed as
 *     typed pages, and nothing should be able to write one as a page;
 *   - `knowledge/pages/`, the flat directory the filing migration empties.
 *     `pages` is a place, never a type, and keeping it out here is what makes
 *     "typed path OR legacy flat" two clauses instead of one — see
 *     `isLegacyFlatPage`, which is the only way to name that shape.
 */
export const pagePathRe =
  /^knowledge\/(?!pages\/)(?:[a-z0-9][a-z0-9-]*\/){1,3}[a-z0-9][a-z0-9-]*\.md$/;

/**
 * Wider than `pagePathRe`, deliberately — it admits the leading dash and the
 * unbounded length that the page grammar refuses. This is a RECOGNIZER, and its
 * failure mode is the opposite one: a flat page it does not recognize is a page
 * the migration cannot file, stranded in a directory nothing else reads. It
 * still holds the part that is actually the jail (no `.`, no `/`), so it is no
 * way around the traversal rules — only a way to name what is already on disk.
 */
const LEGACY_FLAT_PAGE_RE = /^knowledge\/pages\/[a-z0-9-]+\.md$/;

/**
 * The pre-Phase-2 flat page, `knowledge/pages/<slug>.md` — the only shape the
 * Phase 1 editor could write. Recognized so a page can be read, moved and
 * retired before the filing migration reaches it; never so one can be written.
 * Disjoint from `pagePathRe` by construction, so a caller may ask both.
 */
export const isLegacyFlatPage = (path: string): boolean => LEGACY_FLAT_PAGE_RE.test(path);

/**
 * A page's type is the folder it sits in — the last folder segment, whatever
 * that folder is. There is no closed list: a folder the reconciler invented
 * (`decisions/`) types exactly like a seeded one, which is what "seeds, not
 * cages" means in code.
 *
 * A leading `memory/` and `knowledge/` are both optional, so this reads a page
 * path spelled any of the ways this codebase holds one. What comes back:
 *   - `knowledge/entities/people/ankur.md` → `people`;
 *   - `knowledge/learnings/2026-08-29-x.md` → `learnings`, unchanged from Phase 1;
 *   - `knowledge/pages/ankur.md` → `pages`, honestly — a page the migration has
 *     not filed yet has no type of its own, and `isLegacyFlatPage` is the
 *     predicate that says so; answering it here too would be a second truth;
 *   - a root file (`knowledge/index.md`, `how-you-work.md`) → `''`, because it
 *     is not a typed page and no folder name should be invented for it.
 */
export const pageTypeOf = (path: string): string => {
  const belowRoot = path.startsWith(`${MEMORY_ROOT}/`) ? path.slice(MEMORY_ROOT.length + 1) : path;
  const rel = belowRoot.startsWith(`${KNOWLEDGE_DIR}/`)
    ? belowRoot.slice(KNOWLEDGE_DIR.length + 1)
    : belowRoot;
  const cut = rel.lastIndexOf('/');
  return cut < 0 ? '' : rel.slice(0, cut).split('/').pop()!;
};

/**
 * THE FORGET'S MARK (task E6, Ruling 203).
 *
 * A forget never DELETES a page: a page emptied of its claims keeps its frontmatter
 * and one plain line saying what happened, because the moments fold decides what it
 * has already written by page NAME. But a page that survives is a page every reader
 * still walks — the kernel names it in Recent Learnings (from the filename alone), and
 * index.md lists it on the map. So the page carries this key, and every reader that
 * renders a page to a person skips it.
 *
 * A BOOLEAN ON THE PAGE, not a second store: the mark travels with the bytes, through
 * git history, through the published bundle, into any future pass — a list of forgotten
 * pages kept anywhere else is a list that can drift from the pages it names.
 *
 * It is page STRUCTURE, not a claim about the user's world: the gate does not ask it
 * for a citation and the index never renders it as a fact.
 */
export const FORGOTTEN_KEY = 'forgotten';

/** Has a forget emptied this page? Read off the parsed frontmatter, `true` only —
 *  a page that says anything else here has not been marked by the forget rail. */
export const isForgottenPage = (frontmatter: Record<string, unknown> | undefined | null): boolean =>
  frontmatter?.[FORGOTTEN_KEY] === true;

/**
 * The frontmatter key every page may carry without a cites: entry: the page's one-line
 * summary (brain articles, spec §3.3). It restates the page's lead, whose sentences
 * carry their own cites, so the gate asks it for none.
 */
export const PAGE_SUMMARY_KEY = 'description';

/**
 * A page's WORDS, with its frontmatter spine taken off (Ruling 242).
 *
 * Every filed page opens with a `---` fenced YAML block — the validator makes it
 * mandatory — and markdown has no idea it is machinery: the opening fence is a
 * thematic break and the keys under it close as a setext heading, so raw page
 * bytes handed to any renderer come out as a rule and a heading reading
 * `title: … type: … updated: …`. This is the one split that stops that.
 *
 * BODY ONLY, ON PURPOSE. The frontmatter CODEC is the agent SDK's `parsePage`,
 * which reads the block as YAML with a legacy fallback and coerces the spine keys
 * to their declared types. Nothing that merely RENDERS a page needs any of that,
 * and the browser cannot have it — the SDK is a server package that pulls the
 * whole kernel in with it. So the reading surfaces get this: the same fence
 * grammar, the body half of it, and no second `---` split written by hand.
 *
 * DEGRADE-SAFE, exactly like the codec: a page with no fences, an unterminated
 * one, or anything else that is not a block is returned whole. A page is never
 * shown less of itself because its spine was malformed.
 */
export function pageBody(raw: string): string {
  const match = /^---\r?\n[\s\S]*?\r?\n---\r?\n?([\s\S]*)$/.exec(raw);
  return match ? match[1].replace(/^\r?\n/, '') : raw;
}

/**
 * Longest a title may be, everywhere it is read (Ruling 258). A title is a NAME,
 * not a page: the clamp is what keeps one hostile line from being the size of a
 * listing payload, a map line or an `<h1>`. ONE value, so two surfaces cannot
 * shorten the same page differently.
 */
export const TREE_TITLE_MAX_CHARS = 200;

/** The leading frontmatter block, if the page opens with one: group 1 is the
 *  spine, group 2 the body. The SAME fence grammar `pageBody` splits on. */
const LEADING_FENCE_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;
/** The spine's own `title:` line. The space after the colon is optional — YAML
 *  does not require it and a page in the wild does not always write it. */
const SPINE_TITLE_RE = /^title:[ \t]*(.*)$/m;
/** A heading line, any level markdown admits. Seven hashes is not a heading. */
const HEADING_RE = /^#{1,6}[ \t]+(.+)$/;
/** A fenced code block's delimiter, either spelling. */
const CODE_FENCE_RE = /^[ \t]*(?:```|~~~)/;

/** A stem that opens with an ISO date — `2026-08-21-…` (Ruling 264). The whole
 *  date is group 1; whatever separator follows it is consumed with the match. */
const ISO_DATE_PREFIX_RE = /^(\d{4}-\d{2}-\d{2})(?:[-_]+|$)/;

/** One matched pair of surrounding quotes, as a YAML scalar may wear. */
function unquote(value: string): string {
  if (value.length < 2) return value;
  const first = value[0]!;
  if (first !== '"' && first !== "'") return value;
  return value[value.length - 1] === first ? value.slice(1, -1) : value;
}

/**
 * A file stem as a person would write it: `keshav-musunuri` → `Keshav Musunuri`.
 *
 * A WORD THAT ALREADY CARRIES A CAPITAL IS LEFT ALONE. Title Case applied to
 * everything turns `Maya O'Brien` into `Maya O'brien`, and a name spelled wrong
 * is worse than a name not dressed up. Only an all-lowercase word — which is
 * what a slug is made of — gets its capital.
 */
export function humaniseStem(stem: string): string {
  // AN ISO DATE PREFIX STAYS A DATE (Ruling 264). A learning page is named for
  // the day it was learned, and splitting `2026-08-21` into words made the one
  // part of the name that was already readable unreadable. The prefix is kept
  // verbatim; the rest is humanised behind it.
  const dated = ISO_DATE_PREFIX_RE.exec(stem);
  if (dated) {
    const rest = humaniseStem(stem.slice(dated[0].length));
    return rest ? `${dated[1]} ${rest}` : dated[1]!;
  }
  return stem
    .split(/[-_]+/)
    .filter((w) => w.length > 0)
    .map((w) => (w === w.toLowerCase() ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/**
 * WHAT A PAGE IS CALLED — the one title grammar (Ruling 258).
 *
 * Four surfaces read a page's name and three of them spelled the rule
 * differently: the listing's shell pass matched `^(title:|# )` on RAW BYTES, so
 * a YAML comment on the first spine line beat the real heading; the browser's
 * read took `#{1,6}` but not the quotes a scalar may wear; the map's took
 * `^#\s+`, so a page whose only heading is `##` fell through to its file name.
 * One page could be called three things on three screens. This is the one
 * implementation; every other reading of a title is deleted in its favour.
 *
 * IN ORDER:
 *   1. `title:` INSIDE the leading `---` block and nowhere else. Inside is what
 *      makes a `#` line there a YAML comment rather than a heading, and what
 *      keeps a line of prose reading `title: …` from naming the page.
 *   2. else the first heading OUTSIDE the fence and outside a code block — a
 *      loop file, a learning, a legacy flat page, all of which carry no spine.
 *      A ``` block is skipped because a heading written inside one is a sample,
 *      not a name.
 *   3. else the humanised stem of `path`. Which is why this cannot answer
 *      nothing, and why every caller is spared a fallback of its own.
 *
 * EVERY BRANCH IS CLAMPED to `maxChars`, and trimmed after the clamp so a cut
 * never leaves a trailing space.
 *
 * NOT A YAML PARSE, on purpose — the codec is the agent SDK's `parsePage`, a
 * server package the browser cannot have. The scalar as written, unquoted once
 * and trimmed; a title needing more YAML than that is one the validator refuses.
 */
export function pageTitleFromMarkdown(raw: string, path: string, maxChars: number): string {
  const declared = declaredPageTitle(raw, maxChars);
  if (declared !== null) return declared;

  const stem = path.slice(path.lastIndexOf('/') + 1).replace(/\.md$/i, '');
  return (humaniseStem(stem) || stem).slice(0, maxChars).trim();
}

/**
 * WHAT THE PAGE ITSELF SAYS IT IS CALLED — branches 1 and 2 above, and null
 * where the third would take over.
 *
 * THE HALF THE SHELL CAN ANSWER. The listing's awk pass prints a row for a page
 * that declares a name and nothing for one that does not (`treeTitleAwkProgram`),
 * because the stem branch needs `humaniseStem` and belongs to the reader. So the
 * grammar has always had two halves; this names the seam rather than adding a
 * second grammar. Ruling 316 gave it a second caller — the workspace publisher
 * composes a listing in TypeScript over a local root and must emit exactly the
 * rows the shell pass would — and a private copy of these two branches there is
 * the drift Ruling 258 exists to prevent.
 *
 * `pageTitleFromMarkdown` is still the only way to get a title that always
 * answers; this one is for the callers whose contract is "declared, or nothing".
 */
export function declaredPageTitle(raw: string, maxChars: number): string | null {
  const clamp = (value: string): string => value.slice(0, maxChars).trim();
  const fence = LEADING_FENCE_RE.exec(raw);

  if (fence) {
    const stated = unquote(SPINE_TITLE_RE.exec(fence[1]!)?.[1]?.trim() ?? '').trim();
    if (stated) return clamp(stated);
  }

  let inCode = false;
  for (const line of (fence ? fence[2]! : raw).split('\n')) {
    const text = line.replace(/\r$/, '');
    if (CODE_FENCE_RE.test(text)) {
      inCode = !inCode;
      continue;
    }
    if (inCode) continue;
    const heading = HEADING_RE.exec(text)?.[1]?.trim();
    if (heading) return clamp(heading);
  }

  return null;
}

/**
 * How many parts of one chain any surface will treat as parts.
 *
 * ONE VALUE, and it bounds the SAME set on every side: the collector below stops
 * here, and the file read's fold probes no further. A part past it is neither
 * hidden nor folded — it stays on the map and the tree under its own title,
 * which is what keeps "the last copy of a page's words is never off the map"
 * true by construction rather than by two rules agreeing.
 */
export const MAX_CHAIN_PARTS = 20;

/** The splitter's own title suffix — `<Title> (continued 2)`, verbatim. The
 *  number is captured, because it has to MATCH the filename's. */
const CONTINUED_TITLE_RE = / \(continued (\d+)\)$/;

/** A part's file stem: `<head stem>-<n>`. The number is captured for the same
 *  reason. */
const CONTINUATION_STEM_RE = /^(.+)-(\d+)$/;

/** The part number a page's filename claims, or null — `<stem>-<n>.md` below
 *  `headPath`'s own folder and stem. */
function partNumberOf(headPath: string, partPath: string): number | null {
  if (!partPath.endsWith('.md') || !headPath.endsWith('.md')) return null;
  const headStem = headPath.slice(0, -'.md'.length);
  const partStem = partPath.slice(0, -'.md'.length);
  const marked = CONTINUATION_STEM_RE.exec(partStem);
  // The same folder AND the same stem come for free: the head's whole path is
  // the prefix the part's name must extend.
  if (!marked || marked[1] !== headStem) return null;
  const n = Number(marked[2]);
  // `-1` continues nothing — the head IS part one — and a number past the cap is
  // not a part on any surface.
  return n >= 2 && n <= MAX_CHAIN_PARTS ? n : null;
}

/**
 * IS THIS PAGE A PART OF THAT ONE — the one chain evidence (Ruling 263).
 *
 * A page past the budget is continued into `<stem>-<n>.md` beside the head, with
 * a minimal spine titled `<Title> (continued <n>)`. Two surfaces have to agree
 * about which pages those are: the ones that LIST pages hide them, and the file
 * read folds them back into the head. They used to hold different evidence — the
 * hide read the filename and the title, the fold walked `Continued in [[…]]`
 * trailers — so "the head exists" and "the head reaches it" were different
 * questions, and a part could be hidden from the map while its words were never
 * served anywhere. Trailers are still written and still read as text; they are
 * no longer evidence about what a chain IS.
 *
 * MEMBERSHIP, NEVER A WALK, because the numbering has GAPS. The splitter steps
 * past a continuation number already worn ANYWHERE in the tree (Ruling 35 — the
 * gate's duplicate-filename rule is tree-wide, and a refusal wedges the whole
 * pass), so a head may continue straight into `-3`, or run `-2`, `-4`. Any walk
 * that stops at the first missing number drops real parts on the floor.
 *
 * BOTH MARKS, AND THE SAME `n` IN EACH. The filename says `-<n>`, the title says
 * `(continued <n>)`. Either alone is a real page that happens to look like a
 * part — a second person filed at `keshav-musunuri-2.md`, or a page genuinely
 * called "The story so far (continued 2)" — and a page whose two numbers
 * disagree is not one the splitter wrote.
 */
export function isChainPart(headPath: string, partPath: string, partTitle: string): boolean {
  const n = partNumberOf(headPath, partPath);
  if (n === null) return false;
  return Number(CONTINUED_TITLE_RE.exec(partTitle)?.[1] ?? NaN) === n;
}

/**
 * EVERY PART THIS HEAD OWNS, in the order the page reads (Ruling 263).
 *
 * The one collector both sides call. A surface that lists pages hides exactly
 * what comes back; the file read folds exactly what comes back, in this order.
 * Pure, so the two cannot drift: given the same head and the same entries it
 * answers the same thing in the browser and on the server.
 *
 * SORTED BY `n`, not by path, because `-11` sorts before `-2` as text and a page
 * folded in that order reads as nonsense. Capped by COUNT at `MAX_CHAIN_PARTS`,
 * which the number bound in `partNumberOf` already implies — both spellings of
 * the cap are the same constant, and saying it twice is what stops one of them
 * drifting.
 */
export function chainPartsOf<T extends { path: string; title: string }>(
  headPath: string,
  entries: readonly T[],
): T[] {
  return entries
    .filter((e) => isChainPart(headPath, e.path, e.title))
    .sort((a, b) => partNumberOf(headPath, a.path)! - partNumberOf(headPath, b.path)!)
    .slice(0, MAX_CHAIN_PARTS);
}

/** The `<stem>-<n>.md` shape a continuation part's FILENAME wears.
 *
 *  It decides nothing — it only names the heads worth asking about, so the sweep
 *  below is one pass over the entries rather than every entry against every
 *  other. Whether a row IS a part is `chainPartsOf`'s answer and only its. */
const PART_NAME_RE = /^(.+)-\d+\.md$/;

/**
 * EVERY ENTRY THAT IS A PART OF SOME HEAD IN THIS SAME SET (Ruling 263).
 *
 * The whole-listing sweep, where `chainPartsOf` is the single-head one. Three
 * surfaces ask it: the brain index compiler skips these rows, the file read folds
 * them into their head, and the Brain's own lists and counts hide them — so the
 * set returned here is exactly the set the page read folds, which is what makes
 * hidden ⇔ folded a property rather than an intention.
 *
 * A PART WHOSE HEAD IS ABSENT IS NOT HIDDEN, and neither is one past the cap.
 * That falls out of asking only about heads this set carries: those words would
 * otherwise be on no row at all.
 *
 * ASKED ON THE DECLARED TITLE, never a humanised file name — "Keshav musunuri 2"
 * could not carry the splitter's own `(continued 2)` suffix, so a caller with
 * optional titles hands the empty string rather than its own fallback label.
 */
export function hiddenChainParts(entries: readonly { path: string; title: string }[]): Set<string> {
  const paths = new Set(entries.map((e) => e.path));
  // A page can only be a part if its own name carries the mark; deriving the
  // head it would belong to is what keeps this from being quadratic.
  const heads = new Set<string>();
  for (const entry of entries) {
    const marked = PART_NAME_RE.exec(entry.path);
    if (marked && paths.has(`${marked[1]}.md`)) heads.add(`${marked[1]}.md`);
  }

  const hidden = new Set<string>();
  for (const head of heads) {
    for (const part of chainPartsOf(head, entries)) hidden.add(part.path);
  }
  return hidden;
}

/**
 * Mint a citation — Law 3's atom: `ev:<door>[:<app>]:<id>`.
 *
 * Only `sense` nests an app (the connector an observation came through), and
 * the overloads make that the type's business: an app on any other door is a
 * compile error. It has to be, because the parser would read that segment as
 * the head of the id and the citation would not round-trip.
 *
 * A door held in a variable still mints without an app. Narrow it to `'sense'`
 * first if you have one to pass.
 */
export function citeId(door: 'sense', id: string, app?: string): string;
export function citeId(door: EvidenceDoor, id: string): string;
export function citeId(door: EvidenceDoor, id: string, app?: string): string {
  return app ? `ev:${door}:${app}:${id}` : `ev:${door}:${id}`;
}

const isDoor = (value: string): value is EvidenceDoor =>
  (TREE.evidence.doors as readonly string[]).includes(value);

/**
 * Read a citation, or `null` if it is not one. The only sanctioned way to
 * validate a citation — nothing else should match on the string shape.
 *
 * Strict in the frame, permissive in the tail: the prefix and door must be
 * exact, and no segment may be empty, but the id keeps any colons it carries
 * (provider ids are not ours to reformat). The one cost of that trade is that
 * a sense id containing a colon and no app reads its first segment as the app
 * — mint those with an explicit app.
 */
export const parseCiteId = (
  raw: string,
): { door: EvidenceDoor; app?: string; id: string } | null => {
  const segments = raw.split(':');
  if (segments.length < 3 || segments[0] !== 'ev') return null;

  const door = segments[1]!;
  if (!isDoor(door)) return null;

  if (door === 'sense' && segments.length >= 4) {
    const app = segments[2]!;
    const id = segments.slice(3).join(':');
    return app && id ? { door, app, id } : null;
  }

  const id = segments.slice(2).join(':');
  return id ? { door, id } : null;
};

/**
 * EVERY CITATION A PIECE OF TEXT CARRIES, in first-seen order, with where each
 * one sits in it.
 *
 * DISCOVERY IS A SHAPE; THE VERDICT IS `parseCiteId`'s. The pattern only says
 * where an `ev:`-shaped token STOPS — at whitespace, or at the bracket, quote or
 * backtick that closed around it — and sentence punctuation and emphasis markup
 * fall off its tail before the parser looks, so `(ev:task:a1).` reads as the
 * citation and not as the citation plus the full stop. Nothing here decides what
 * a citation IS.
 *
 * THE INDEX IS PART OF THE ANSWER. A caller that REMOVES citations from prose —
 * the Brain's front door does, because a summary is words and a cite id is an
 * address — needs to know where each one sat; finding them a second time by
 * string search would move the wrong one on a page that cites the same evidence
 * twice.
 *
 * A REGEX PER CALL, never a module constant: a `/g` pattern carries `lastIndex`,
 * and one shared object is one caller's scan moving another caller's starting
 * point.
 */
export function citesInText(text: string): Array<{ cite: string; index: number }> {
  const found: Array<{ cite: string; index: number }> = [];
  for (const match of text.matchAll(/ev:[^\s`\])}>'"]+/g)) {
    const cite = match[0].replace(/[.,;:!?*_]+$/, '');
    if (parseCiteId(cite)) found.push({ cite, index: match.index });
  }
  return found;
}

/**
 * Longest a sanitized path segment may be. NAME_MAX is 255 on every filesystem
 * the volume could be; a ~230-char filename-derived id plus `.md.tmp` would
 * make the stub write fail EVERY pass and force the full body read forever.
 * 200 leaves room for the hash below, `.md` and the writer's `.tmp`.
 */
const SEGMENT_MAX = 200;

/**
 * FNV-1a, 32-bit, as eight hex digits. Dependency-free on purpose: this module
 * is shared with the browser, where `node:crypto` is not. Not security — just
 * enough to keep two long ids that agree on their first 200 characters apart.
 */
function shortHash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Anything outside the safe alphabet becomes a dash, and a segment longer than
 *  the clamp keeps its head plus a short hash of the ORIGINAL. Ids are not ours
 *  to reformat, and directory names are neither free-form nor unbounded, so the
 *  three meet here; the body carries the verbatim id. */
const fsSafe = (segment: string): string => {
  const safe = segment.replace(/[^A-Za-z0-9._-]/g, '-');
  return safe.length > SEGMENT_MAX ? `${safe.slice(0, SEGMENT_MAX)}-${shortHash(segment)}` : safe;
};

/**
 * Where an evidence item's markdown stub lives in the tree, relative to the
 * memory root: `evidence/<door>/<id>.md`, and `evidence/sense/<app>/<id>.md`
 * for a sense.
 *
 * THE PATH IS CONVENIENCE; THE BODY IS THE CITATION. Ids carry colons and
 * slashes (they come from providers), which no filesystem wants in a name, so
 * every unsafe character collapses to a dash here. That is lossy on purpose and
 * it is why the VERBATIM cite id is written INSIDE the stub: a reader resolves a
 * citation by the id in the body, never by reversing this path. Two ids that
 * differ only in their unsafe characters land on one file — a real collision,
 * accepted, because the store (keyed by the exact cite id) is the truth and the
 * stub is a rebuildable projection of it.
 *
 * The app overload mirrors `citeId`: only `sense` nests an app, and the compiler
 * says so. The two must agree about where an app may appear, or a stub would sit
 * at a path its own citation does not describe.
 */
export function evidenceStubPath(door: 'sense', id: string, app?: string): string;
export function evidenceStubPath(door: EvidenceDoor, id: string): string;
export function evidenceStubPath(door: EvidenceDoor, id: string, app?: string): string {
  const dir =
    door === 'sense' && app
      ? `${TREE.evidence.root}/${door}/${fsSafe(app)}`
      : `${TREE.evidence.root}/${door}`;
  return `${dir}/${fsSafe(id)}.md`;
}
